import { useEffect } from "react";

/**
 * Tiny dependency-free <head> manager. Sets `<title>`, `<meta name=description>`,
 * canonical link, and OG/Twitter tags on mount; reverts on unmount.
 *
 * Why not react-helmet-async? One less dependency, our SPA has a single
 * <SeoHead/> active at a time so the cleanup story is simple.
 *
 * Accepts a `structuredData` object that gets JSON-stringified into a
 * <script type="application/ld+json"> for crawlers (Article, Dataset, etc.).
 */
export default function SeoHead({ title, description, canonical, image,
                                  type = "website", structuredData = null,
                                  noindex = false }) {
  useEffect(() => {
    const ogPrefix = "og:";
    const twPrefix = "twitter:";
    const setMeta = (selector, attr, val) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.match(/\[(.+?)="(.+?)"\]/).slice(1);
        el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
      return el;
    };
    const created = [];
    const prevTitle = document.title;
    if (title) document.title = title;
    if (description) {
      created.push(setMeta('meta[name="description"]', "content", description));
      created.push(setMeta(`meta[property="${ogPrefix}description"]`, "content", description));
      created.push(setMeta(`meta[name="${twPrefix}description"]`, "content", description));
    }
    if (title) {
      created.push(setMeta(`meta[property="${ogPrefix}title"]`, "content", title));
      created.push(setMeta(`meta[name="${twPrefix}title"]`, "content", title));
    }
    if (image) {
      created.push(setMeta(`meta[property="${ogPrefix}image"]`, "content", image));
      created.push(setMeta(`meta[name="${twPrefix}image"]`, "content", image));
    }
    created.push(setMeta(`meta[property="${ogPrefix}type"]`, "content", type));
    if (noindex) created.push(setMeta('meta[name="robots"]', "content", "noindex, nofollow"));
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }
    let ldScript = null;
    if (structuredData) {
      ldScript = document.createElement("script");
      ldScript.setAttribute("type", "application/ld+json");
      ldScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(ldScript);
    }
    return () => {
      document.title = prevTitle;
      if (ldScript) ldScript.remove();
      // We intentionally don't delete the meta tags — leaving them lets the
      // next page overwrite them cleanly without flicker.
    };
  }, [title, description, canonical, image, type, noindex, JSON.stringify(structuredData || {})]);
  return null;
}
